import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RiskBadge from "@/components/RiskBadge";
import { BangaloreHeatmap } from "@/components/BangaloreHeatmap";
import { toast } from "sonner";
import {
  Shield,
  Phone,
  AtSign,
  Image as ImageIcon,
  TrendingUp,
  AlertTriangle,
  FileDown,
} from "lucide-react";

function StatCard({ label, value, hint, icon: Icon, tone = "slate", testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 bg-white rounded-md p-4 flex flex-col gap-2"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
          {label}
        </span>
        <Icon className={`h-4 w-4 text-${tone}-500`} />
      </div>
      <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight">
        {value ?? "—"}
      </div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .finally(() => setLoading(false));
  }, []);

  const rb = stats?.risk_breakdown || { high: 0, medium: 0, low: 0 };
  const totalRisk = rb.high + rb.medium + rb.low || 1;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
            Overview
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-1">
            Investigator dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-2 max-w-2xl">
            Patterns of repeated phones, shared images and similar wording detected
            across collected advertisements in Bangalore.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            data-testid="download-police-brief"
            onClick={async () => {
              try {
                const res = await api.get("/police-brief.pdf", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Project_MUKTI_Police_Brief.pdf";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Police brief downloaded");
              } catch {
                toast.error("Could not generate PDF");
              }
            }}
            className="inline-flex items-center gap-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 rounded-md"
            title="One-page A4 brief designed to hand to a police officer"
          >
            <FileDown className="h-3.5 w-3.5" /> Police brief (PDF)
          </button>
          {user?.role === "admin" && (
            <button
              type="button"
              data-testid="send-police-roster"
              onClick={async () => {
                try {
                  const { data } = await api.post("/police-brief/send");
                  if (data.sent) {
                    toast.success(`Police brief mailed to ${data.recipients} officer(s)`);
                  } else if (data.disabled) {
                    toast.warning("Set BREVO_API_KEY on the backend to enable roster sends.");
                  } else {
                    toast.error(data.reason || "Send failed");
                  }
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Send failed");
                }
              }}
              className="inline-flex items-center gap-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 rounded-md"
              title="Email the latest police brief PDF to every officer on the roster"
            >
              <FileDown className="h-3.5 w-3.5" /> Send to police roster
            </button>
          )}
          <button
            type="button"
            data-testid="download-investor-pdf"
            onClick={async () => {
              try {
                const res = await api.get("/investor-overview.pdf", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Project_MUKTI_Investor_Overview.pdf";
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Investor overview downloaded");
              } catch {
                toast.error("Could not generate PDF");
              }
            }}
            className="inline-flex items-center gap-1.5 text-xs border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md"
          >
            <FileDown className="h-3.5 w-3.5" /> Investor overview (PDF)
          </button>
          <div className="text-xs text-slate-500 font-mono">
            {loading ? "loading…" : `${stats?.total_ads || 0} ads in scope`}
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid="stat-total-ads"
          label="Total ads"
          value={stats?.total_ads}
          hint="Across all sources"
          icon={Shield}
        />
        <StatCard
          testid="stat-phones"
          label="Unique phones"
          value={stats?.unique_phones}
          hint={`${stats?.top_phones?.length || 0} reused across ads`}
          icon={Phone}
        />
        <StatCard
          testid="stat-usernames"
          label="Unique usernames"
          value={stats?.unique_usernames}
          icon={AtSign}
        />
        <StatCard
          testid="stat-new-week"
          label="New this week"
          value={stats?.new_this_week}
          hint="Last 7 days"
          icon={TrendingUp}
        />
      </section>

      <section className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-slate-200 bg-white rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg tracking-tight">
              Risk distribution
            </h2>
            <span className="text-xs text-slate-500 font-mono">
              {rb.high + rb.medium + rb.low} ads scored
            </span>
          </div>
          <div className="space-y-3">
            {[
              { key: "high", label: "High risk", color: "bg-red-700" },
              { key: "medium", label: "Medium risk", color: "bg-amber-600" },
              { key: "low", label: "Low risk", color: "bg-teal-700" },
            ].map(({ key, label, color }) => {
              const v = rb[key] || 0;
              const pct = Math.round((v / totalRisk) * 100);
              return (
                <div key={key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{label}</span>
                    <span className="font-mono text-slate-500">
                      {v} ads · {pct}%
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-md overflow-hidden">
                    <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border border-slate-200 bg-white rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg tracking-tight">
              Top reused phones
            </h2>
            <Phone className="h-4 w-4 text-slate-400" />
          </div>
          <ul className="space-y-2">
            {(stats?.top_phones || []).map((p) => (
              <li
                key={p.value}
                className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0 last:pb-0"
              >
                <Link
                  to={`/lookup/phone?q=${encodeURIComponent(p.value)}`}
                  className="font-mono hover:text-blue-700"
                  data-testid={`top-phone-${p.value}`}
                >
                  {p.value}
                </Link>
                <span className="text-xs font-mono text-red-700">
                  {p.count} ads
                </span>
              </li>
            ))}
            {(!stats?.top_phones || stats.top_phones.length === 0) && (
              <li className="text-xs text-slate-400">No reused phones detected.</li>
            )}
          </ul>
        </div>
      </section>

      <BangaloreHeatmap />

      <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="language-coverage">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg tracking-tight">
            Language coverage
          </h2>
          <span className="text-xs font-mono text-slate-400">
            {(stats?.language_breakdown?.en || 0) + (stats?.language_breakdown?.kn || 0) + (stats?.language_breakdown?.mixed || 0) + (stats?.language_breakdown?.unknown || 0)} ads
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { key: "en", label: "English", tone: "bg-slate-100 text-slate-800" },
            { key: "kn", label: "Kannada", tone: "bg-amber-100 text-amber-800" },
            { key: "mixed", label: "Mixed", tone: "bg-purple-100 text-purple-800" },
            { key: "unknown", label: "Unknown", tone: "bg-slate-50 text-slate-500 border border-dashed border-slate-200" },
          ].map((l) => (
            <Link
              key={l.key}
              to={l.key === "unknown" ? "/ads" : `/search?q=${encodeURIComponent("")}&lang=${l.key}`}
              data-testid={`lang-${l.key}-card`}
              className={`rounded-md px-4 py-3 ${l.tone} hover:opacity-90 transition-opacity`}
              onClick={(e) => { if (l.key === "unknown") e.preventDefault(); }}
            >
              <div className="text-2xl font-display font-bold tracking-tight">
                {stats?.language_breakdown?.[l.key] ?? 0}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-wider mt-1">{l.label}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border border-slate-200 bg-white rounded-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-700" /> High-risk leads
          </h2>
          <Link
            to="/ads?level=high"
            className="text-xs text-blue-700 hover:underline font-medium"
            data-testid="see-all-high-risk"
          >
            See all
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(stats?.high_risk_ads || []).map((a) => (
            <Link
              to={`/ads/${a.id}`}
              key={a.id}
              data-testid={`high-risk-${a.id}`}
              className="flex items-center justify-between px-5 py-3 hover:bg-slate-50"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{a.title}</div>
                <div className="text-xs text-slate-500 font-mono">
                  {a.locality || "Unknown locality"}
                </div>
              </div>
              <RiskBadge level={a.level} score={a.score} />
            </Link>
          ))}
          {(!stats?.high_risk_ads || stats.high_risk_ads.length === 0) && (
            <div className="px-5 py-6 text-sm text-slate-500">
              No high-risk leads yet.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
